"use client"

import { useState, useEffect, useRef } from "react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Check, X } from "lucide-react"
import type { FieldConfiguration } from "@/types/workflow"
import { CustomDropdownField } from "@/components/custom-dropdown-field"
import { ModernDatePicker } from "@/components/modern-date-picker"
import { useLanguage } from '@/context/language-context'
import { getLocalizedField } from '@/utils/language-utils'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { useToast } from '@/hooks/use-toast'

interface EditableCellProps {
  value: any
  column: FieldConfiguration
  rowId: string
  onSave: (value: any) => void
  onCancel: () => void
}

export function EditableCell({ value, column, rowId, onSave, onCancel }: EditableCellProps) {
  const [editValue, setEditValue] = useState(value)
  const { language } = useLanguage()
  const { trackCellAction } = useWorkflowTracking()
  const { toast } = useToast()
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  // Parse UTC date string to preserve exact date
  const parseUTCDate = (dateStr: string) => {
    if (!dateStr) return null
    const dateOnly = dateStr.split('T')[0]
    return dateOnly
  }

  // Format date for saving - convert local midnight to UTC
  const formatDateForSave = (dateStr: string | null) => {
    if (!dateStr) return null
    // Parse as local date at midnight
    const [year, month, day] = dateStr.split('-').map(Number)
    const localDate = new Date(year, month - 1, day, 0, 0, 0, 0)
    // Convert to ISO string (which is in UTC)
    return localDate.toISOString()
  }

  // Validate field format if fieldFormatting is provided
  const validateFieldFormat = (value: any): boolean => {
    if (!column.fieldFormatting || !value) return true; // Skip validation if no format or empty value
    
    try {
      const regex = new RegExp(column.fieldFormatting);
      return regex.test(String(value));
    } catch (error) {
      console.warn('Invalid regex pattern in fieldFormatting:', column.fieldFormatting);
      return true; // Allow if regex is invalid
    }
  }

  const handleSave = (newValue: any) => {
    // Validate field format before saving
    if (!validateFieldFormat(newValue)) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: `Invalid format for ${getLocalizedField(column, 'columnName', language)}`,
      });
      return;
    }
    
    // Track the cell edit
    trackCellAction('edit', column.columnCode)
    onSave(newValue)
  }

  const handleCancel = () => {
    // Track the cancel action (using edit action type since cancel is not in the allowed types)
    trackCellAction('edit', column.columnCode)
    onCancel()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave(editValue)
    } else if (e.key === "Escape") {
      handleCancel()
    }
  }

  // Handle different column types based on customValuesType
  switch (column.customValuesType?.toLowerCase() || column.columnTypeCode?.toLowerCase()) {
    case "text":
    case "email":
    case "link":
      return (
        <div className="flex items-center gap-1">
          <Input
            ref={inputRef}
            value={editValue || ""}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="h-8 min-w-[120px]"
          />
          <div className="flex items-center">
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => handleSave(editValue)}>
              <Check className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={handleCancel}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )

    case "date":
      return (
        <div className="flex items-center gap-1">
          <div className="min-w-[180px]">
            <ModernDatePicker 
              value={parseUTCDate(value)} 
              onChange={(date) => {
                setEditValue(date)
                if (date) {
                  onSave(formatDateForSave(date))
                }
              }} 
              autoOpen 
            />
          </div>
          <div className="flex items-center">
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => handleSave(formatDateForSave(editValue))}>
              <Check className="h-4 w-4 text-green-600" />
            </Button>
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={handleCancel}>
              <X className="h-4 w-4 text-red-500" />
            </Button>
          </div>
        </div>
      )

    case "yesno":
      return (
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch
              checked={!!editValue}
              onCheckedChange={(checked) => setEditValue(checked)}
              id={`boolean-switch-${rowId}-${column.fieldConfigurationId}`}
            />
            <label htmlFor={`boolean-switch-${rowId}-${column.fieldConfigurationId}`} className="text-sm">
              {editValue ? "Yes" : "No"}
            </label>
          </div>
          <div className="flex items-center">
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => handleSave(editValue)}>
              <Check className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={handleCancel}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )

    case "dropdown":
      // For dropdown fields with customValues
      if (column.customValues && column.customValues.length > 0) {
        // Format options for the dropdown
        const dropdownOptions = column.customValues.map((customValue) => ({
          value: getLocalizedField(customValue, 'title', language),
          label: getLocalizedField(customValue, 'title', language),
          color: customValue.color,
        }))

        // Convert single value to array for multi-select (if needed)
        const selectedValues = Array.isArray(editValue) ? editValue : editValue ? [editValue] : []

        return (
          <div className="flex items-center gap-1">
            <div className="min-w-[180px]">
              <CustomDropdownField
                options={dropdownOptions}
                selectedValues={selectedValues}
                onChange={(values) => setEditValue(values)}
                useColorCode={true}
                showSearch={false}
              />
            </div>
            <div className="flex items-center">
              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => handleSave(editValue)}>
                <Check className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={handleCancel}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )
      }

      // Fallback for dropdowns without customValues
      return (
        <div className="flex items-center gap-1">
          <Input
            ref={inputRef}
            value={editValue || ""}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="h-8 min-w-[120px]"
          />
          <div className="flex items-center">
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => handleSave(editValue)}>
              <Check className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={handleCancel}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )

    case "number":
      return (
        <div className="flex items-center gap-1">
          <Input
            ref={inputRef}
            type="number"
            value={editValue || ""}
            onChange={(e) => {
              const value = e.target.value;
              // Convert empty string to null, otherwise convert to number
              const numericValue = value === "" ? null : Number(value);
              setEditValue(numericValue);
            }}
            onKeyDown={handleKeyDown}
            className="h-8 min-w-[120px]"
          />
          <div className="flex items-center">
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => handleSave(editValue)}>
              <Check className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={handleCancel}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )

    default:
      // Default to text input for unknown types
      return (
        <div className="flex items-center gap-1">
          <Input
            ref={inputRef}
            value={editValue || ""}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="h-8 min-w-[120px]"
          />
          <div className="flex items-center">
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => handleSave(editValue)}>
              <Check className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={handleCancel}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )
  }
}
